import * as React from "react";
import { Link, styled } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ConfigurationDottedKey } from "@kahunmedical/common-configuration/dist/lib/annotations";
import {
  ConfigMetaData,
  ConfigMetaDataAccess,
} from "@kahunmedical/common-configuration";
import { KahunSettingPresentation } from "../presentaional/kahunSettingPresentation";
import { SettingsButton } from "../presentaional/controls/settingsButton";
import { UseConfigurationSettingsManager } from "../common/useConfigurationSettingsManager";
import { KahnSettingsBreadcrumbs } from "../presentaional/controls/kahnSettingsBreadcrumbs";
import { useHistory, useRouteMatch } from "react-router";

//region [[ Styles ]]

const KahunSettingsEditorView = styled("div")(({ theme }) => ({
  // display: "flex",
  // flexFlow: "column",
  // height: "100%",
  // [theme.breakpoints.down("xs")]: {
  //   padding: 0,
  // },
}));

const SettingsActions = styled("div")({
  borderTop: "1px solid #E6E4FF",
  padding: "25px 0",
  display: "flex",
  flexFlow: "row-reverse",
});

const RestoreDefaultsButton = styled(SettingsButton)({});

const KahunSettingsList = styled("div")({
  display: "flex",
  flexFlow: "row",
  flexWrap: "wrap",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface KahunSettingEditorProps {
  root: ConfigurationDottedKey;
  showBackoffice?: boolean;
  settingsManager: UseConfigurationSettingsManager;
  onResetDefaultClick: (root: ConfigurationDottedKey) => void;
  usePath?: boolean;
  filter?: ConfigMetaData[];
  highlight?: string;
  onExpandSubCategory?: (root: ConfigurationDottedKey) => void;
  disabledItems?: string[];
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const KahunSettingEditor = ({
  root,
  usePath,
  ...props
}: KahunSettingEditorProps) => {
  const history = useHistory();

  const match = useRouteMatch<{ key: string }>();

  const [currentRoot, setCurrentRoot] = useState<ConfigurationDottedKey>(
    match.params.key && usePath ? match.params.key : null,
  );

  const [metadata, setMetadata] = useState<ConfigMetaData[]>();

  const [loading, setLoading] = useState(false);

  const markRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (match.params.key && usePath) setCurrentRoot(match.params.key);
    else setCurrentRoot(root);
  }, [root, match.params.key, usePath, setCurrentRoot]);

  useEffect(() => {
    if (!loading && markRootRef.current) {
      markRootRef.current.scrollIntoView({
        block: "start",
        inline: "end",
        behavior: "smooth",
      });
    }
  }, [loading, markRootRef.current]);

  // Create accessor for metadata - as metadata is being fetched based on the
  // current root
  const configMetaDataAccess = useRef<ConfigMetaDataAccess>(
    new ConfigMetaDataAccess(),
  );

  const handleRestoreDefaults = () => props.onResetDefaultClick(currentRoot);

  useEffect(() => {
    setLoading(true);
    configMetaDataAccess?.current
      ?.getMetadataForKey(currentRoot)
      .then((md) => {
        if (
          (!md || Object.entries(md).length === 0) &&
          currentRoot?.includes(".")
        ) {
          // If we didn't get anything, its probably not a sub-object -
          // so get the parent markdown and just paint the current
          configMetaDataAccess.current
            .getMetadataForKey(
              currentRoot
                .split(".")
                .slice(0, -1)
                .join("."),
            )
            .then((parentMd) => setMetadata(parentMd));
        } else {
          setMetadata(md);
        }
      })
      .finally(() => setLoading(false));
  }, [currentRoot]);

  const handleSubObjectExpand = (root: ConfigurationDottedKey) => {
    if (props.onExpandSubCategory) {
      props.onExpandSubCategory(root);
      return;
    }
    if (usePath) {
      // props?.changeRoute();
      history.push(root);
      return;
    }
    setCurrentRoot(root);
  };

  const isDisabled = (itemKey: string): boolean => {
    return props.disabledItems?.includes(itemKey);
  };

  return (
    <KahunSettingsEditorView className={"kahun-settings-editor"}>
      <KahnSettingsBreadcrumbs
        root={currentRoot}
        onPathChanged={(newPath) => handleSubObjectExpand(newPath)}
      />
      <KahunSettingsList className={"kahun-settings-list"}>
        {loading && <>Loading...</>}
        {!loading &&
          (
            metadata?.filter?.(
              (md) =>
                !props.filter ||
                props?.filter?.some?.((f) => f.key.startsWith(md.key)),
            ) || []
          ).map((metaData) => {
            const linkToCurrent = currentRoot === metaData.key;
            return (
              <KahunSettingPresentation
                className={
                  linkToCurrent ? "selected" : "kahun-setting-presentation"
                }
                key={metaData.key}
                setting={metaData}
                valueWithSource={props.settingsManager?.getValue(metaData)}
                onValueChanged={(
                  key,
                  value, // Wrap in arrow function since this class `this` isn't bound
                ) => {
                  console.log("calling kahun settings manager", key, value);
                  props.settingsManager.setValue(key, value);
                }}
                onSubObjectExpand={handleSubObjectExpand}
                ref={linkToCurrent ? markRootRef : null}
                highlight={props.highlight}
                disabled={isDisabled(metaData.key)}
              />
            );
          })}
      </KahunSettingsList>
      <SettingsActions className={"settings-actions"}>
        <RestoreDefaultsButton onClick={handleRestoreDefaults}>
          Reset tab to defaults
        </RestoreDefaultsButton>
      </SettingsActions>
    </KahunSettingsEditorView>
  );
};
